import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Upload,
  Image,
  Video,
  Loader2,
  X,
  CheckCircle,
  AlertCircle,
  Images,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState("all");
  const fileInputRef = useRef(null);

  // Multi-file upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadCategory, setUploadCategory] = useState("general");

  // Multi-select state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/gallery`, getAuthHeader());
      setItems(res.data);
      setSelectedItems(new Set()); // Reset selection on refresh
    } catch (err) {
      toast.error("Erreur lors du chargement de la galerie");
    } finally {
      setLoading(false);
    }
  };

  // Filtered items - MUST be defined before selection handlers
  const filteredItems =
    filter === "all"
      ? items
      : items.filter((item) => item.category === filter);

  const categories = ["general", "circuits", "stages", "team"];

  // Selection handlers
  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set(filteredItems.map((item) => item.id));
    setSelectedItems(allIds);
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  const isAllSelected = filteredItems.length > 0 && selectedItems.size === filteredItems.length;
  const isSomeSelected = selectedItems.size > 0 && selectedItems.size < filteredItems.length;

  // Bulk actions
  const bulkToggleVisibility = async (makeVisible) => {
    if (selectedItems.size === 0) return;
    
    const action = makeVisible ? "afficher" : "masquer";
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${selectedItems.size} média(s) ?`)) return;

    setBulkLoading(true);
    let successCount = 0;

    for (const itemId of selectedItems) {
      try {
        await axios.post(
          `${API}/admin/gallery/${itemId}/set-visibility`,
          { is_visible: makeVisible },
          getAuthHeader()
        );
        successCount++;
      } catch (err) {
        console.error(`Error updating ${itemId}:`, err);
      }
    }

    setBulkLoading(false);
    toast.success(`${successCount} média(s) ${makeVisible ? "affichés" : "masqués"}`);
    fetchGallery();
  };

  const bulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (!window.confirm(`Supprimer définitivement ${selectedItems.size} média(s) ?`)) return;

    setBulkLoading(true);
    let successCount = 0;

    for (const itemId of selectedItems) {
      try {
        await axios.delete(`${API}/admin/gallery/${itemId}`, getAuthHeader());
        successCount++;
      } catch (err) {
        console.error(`Error deleting ${itemId}:`, err);
      }
    }

    setBulkLoading(false);
    toast.success(`${successCount} média(s) supprimé(s)`);
    fetchGallery();
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Initialize progress for each file
      const initialProgress = {};
      files.forEach((file, index) => {
        initialProgress[index] = { status: "pending", progress: 0 };
      });
      setUploadProgress(initialProgress);
      setSelectedFiles(files);
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    const newProgress = { ...uploadProgress };
    delete newProgress[index];
    setUploadProgress(newProgress);
  };

  const handleMultiUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Veuillez sélectionner au moins un fichier");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      // Update status to uploading
      setUploadProgress((prev) => ({
        ...prev,
        [i]: { status: "uploading", progress: 0 },
      }));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("title_fr", file.name.split(".")[0]); // Use filename as title
      formData.append("title_en", file.name.split(".")[0]);
      formData.append("category", uploadCategory);

      try {
        await axios.post(`${API}/admin/gallery`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prev) => ({
              ...prev,
              [i]: { status: "uploading", progress },
            }));
          },
        });

        setUploadProgress((prev) => ({
          ...prev,
          [i]: { status: "success", progress: 100 },
        }));
        successCount++;
      } catch (err) {
        setUploadProgress((prev) => ({
          ...prev,
          [i]: { status: "error", progress: 0 },
        }));
        errorCount++;
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} média(s) ajouté(s) avec succès`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} erreur(s) lors de l'upload`);
    }

    // Reset after a short delay to show final status
    setTimeout(() => {
      if (errorCount === 0) {
        setShowUpload(false);
        setSelectedFiles([]);
        setUploadProgress({});
      }
      fetchGallery();
    }, 1500);
  };

  const toggleVisibility = async (itemId) => {
    try {
      await axios.post(
        `${API}/admin/gallery/${itemId}/toggle-visibility`,
        {},
        getAuthHeader()
      );
      toast.success("Visibilité modifiée");
      fetchGallery();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${itemId}`, getAuthHeader());
      toast.success("Média supprimé");
      fetchGallery();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("video/")) {
      return <Video className="h-5 w-5 text-blue-500" />;
    }
    return <Image className="h-5 w-5 text-green-500" />;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "uploading":
        return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
      default:
        return null;
    }
  };

  return (
    <div data-testid="admin-gallery-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif font-bold">Galerie</h1>
          <p className="text-muted-foreground">
            Gérez les photos et vidéos du site
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)} data-testid="add-gallery-btn">
          <Images className="h-4 w-4 mr-2" />
          Ajouter des médias
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedItems.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <span className="font-medium text-primary">
            {selectedItems.size} sélectionné(s)
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => bulkToggleVisibility(true)}
            disabled={bulkLoading}
            className="border-green-500 text-green-700 hover:bg-green-50"
          >
            <Eye className="h-4 w-4 mr-1" />
            Afficher
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => bulkToggleVisibility(false)}
            disabled={bulkLoading}
            className="border-orange-500 text-orange-700 hover:bg-orange-50"
          >
            <EyeOff className="h-4 w-4 mr-1" />
            Masquer
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={bulkDelete}
            disabled={bulkLoading}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Supprimer
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={deselectAll}
            disabled={bulkLoading}
          >
            <X className="h-4 w-4 mr-1" />
            Annuler
          </Button>
        </div>
      )}

      {/* Filters with Select All */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {/* Select All Checkbox */}
        <Button
          variant="outline"
          size="sm"
          onClick={isAllSelected ? deselectAll : selectAll}
          className="mr-2"
        >
          {isAllSelected ? (
            <CheckSquare className="h-4 w-4 mr-1 text-primary" />
          ) : isSomeSelected ? (
            <MinusSquare className="h-4 w-4 mr-1 text-primary" />
          ) : (
            <Square className="h-4 w-4 mr-1" />
          )}
          {isAllSelected ? "Tout désélectionner" : "Tout sélectionner"}
        </Button>

        <div className="h-6 w-px bg-border mx-2" />

        <Button
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
          size="sm"
        >
          Tous ({items.length})
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={filter === cat ? "default" : "outline"}
            onClick={() => setFilter(cat)}
            size="sm"
          >
            {cat} ({items.filter((i) => i.category === cat).length})
          </Button>
        ))}
      </div>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl">
          <Image className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Aucun média</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredItems.map((item) => {
            const isSelected = selectedItems.has(item.id);
            return (
              <div
                key={item.id}
                className={`relative group aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer transition-all ${
                  !item.is_visible ? "opacity-50" : ""
                } ${isSelected ? "ring-4 ring-primary ring-offset-2" : ""}`}
                onClick={() => toggleSelectItem(item.id)}
                data-testid={`gallery-item-${item.id}`}
              >
                {/* Selection Checkbox */}
                <div 
                  className={`absolute top-2 left-2 z-10 transition-opacity ${
                    isSelected || selectedItems.size > 0 ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                  }`}
                >
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                    isSelected ? "bg-primary text-white" : "bg-white/90 border border-gray-300"
                  }`}>
                    {isSelected && <CheckCircle className="h-4 w-4" />}
                  </div>
                </div>

                {item.media_type === "video" ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Video className="h-8 w-8 text-white" />
                  </div>
                ) : null}
                <img
                  src={item.file_url}
                  alt={item.title?.fr}
                  className="w-full h-full object-cover"
                />

                {/* Overlay Actions - Only show when not in selection mode */}
                {selectedItems.size === 0 && (
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVisibility(item.id);
                      }}
                      title={item.is_visible ? "Masquer" : "Afficher"}
                    >
                      {item.is_visible ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteItem(item.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Visibility Badge */}
                {!item.is_visible && (
                  <div className="absolute top-2 right-2">
                    <EyeOff className="h-5 w-5 text-white drop-shadow-lg" />
                  </div>
                )}

                {/* Category Badge */}
                <div className="absolute bottom-2 left-2">
                  <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                    {item.category}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Multi-Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={(open) => {
        if (!uploading) {
          setShowUpload(open);
          if (!open) {
            setSelectedFiles([]);
            setUploadProgress({});
          }
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Images className="h-5 w-5" />
              Ajouter des médias
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <Label>Catégorie pour tous les fichiers</Label>
              <Select
                value={uploadCategory}
                onValueChange={setUploadCategory}
                disabled={uploading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Général</SelectItem>
                  <SelectItem value="circuits">Circuits</SelectItem>
                  <SelectItem value="stages">Stages</SelectItem>
                  <SelectItem value="team">Équipe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File Drop Zone */}
            <div>
              <Label>Fichiers (images ou vidéos) *</Label>
              <div
                className={`mt-2 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  uploading
                    ? "border-muted cursor-not-allowed"
                    : "border-border hover:border-primary"
                }`}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-1">
                  Cliquez pour sélectionner plusieurs fichiers
                </p>
                <p className="text-xs text-muted-foreground">
                  Images (JPG, PNG, WebP) ou Vidéos (MP4, WebM)
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleFilesChange}
                disabled={uploading}
              />
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <Label>Fichiers sélectionnés ({selectedFiles.length})</Label>
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                  >
                    {getFileIcon(file)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      {uploadProgress[index]?.status === "uploading" && (
                        <Progress
                          value={uploadProgress[index]?.progress || 0}
                          className="h-1 mt-1"
                        />
                      )}
                    </div>
                    {uploadProgress[index] ? (
                      getStatusIcon(uploadProgress[index].status)
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => removeFile(index)}
                        disabled={uploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUpload(false)}
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleMultiUpload}
              disabled={uploading || selectedFiles.length === 0}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Uploader {selectedFiles.length > 0 && `(${selectedFiles.length})`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGallery;
